(() => {
  if (window.__riseCardAudioPreviewInitialized) return;
  window.__riseCardAudioPreviewInitialized = true;

  let activeAudio = null;
  let activeButton = null;

  const setButtonState = (button, playing) => {
    if (!button) return;
    button.classList.toggle('is-playing', playing);
    button.setAttribute('aria-pressed', playing ? 'true' : 'false');
    const title = button.dataset.productTitle || 'product';
    button.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} demo: ${title}`);
  };

  const stopActive = () => {
    if (activeAudio) {
      activeAudio.pause();
      try { activeAudio.currentTime = 0; } catch (error) {}
    }
    if (activeButton) setButtonState(activeButton, false);
    activeAudio = null;
    activeButton = null;
  };

  const productJsonUrl = (href) => {
    const url = new URL(href, window.location.origin);
    url.search = '';
    url.hash = '';
    url.pathname = url.pathname.replace(/\/$/, '') + '.js';
    return url.toString();
  };

  const loadDemo = async (button) => {
    if (button.dataset.audioSrc) return button.dataset.audioSrc;
    if (button.dataset.demoMissing === 'true') return null;
    if (button._demoPromise) return button._demoPromise;

    button._demoPromise = fetch(productJsonUrl(button.dataset.productUrl), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
        return response.json();
      })
      .then((product) => {
        const doc = new DOMParser().parseFromString(product.description || '', 'text/html');
        const source = doc.querySelector('audio source[src], audio[src]');
        const src = source ? source.getAttribute('src') : null;
        if (!src) {
          button.dataset.demoMissing = 'true';
          button.hidden = true;
          return null;
        }
        button.dataset.audioSrc = src;
        return src;
      })
      .catch(() => {
        button.dataset.demoMissing = 'true';
        button.hidden = true;
        return null;
      })
      .finally(() => {
        button._demoPromise = null;
      });

    return button._demoPromise;
  };

  const playButton = async (button) => {
    if (button === activeButton && activeAudio) {
      if (activeAudio.paused) {
        try {
          await activeAudio.play();
          setButtonState(button, true);
        } catch (error) {
          setButtonState(button, false);
        }
      } else {
        activeAudio.pause();
        setButtonState(button, false);
      }
      return;
    }

    const src = await loadDemo(button);
    if (!src) return;

    stopActive();
    const audio = new Audio(src);
    audio.preload = 'none';
    activeAudio = audio;
    activeButton = button;
    audio.addEventListener('play', () => {
      if (activeAudio === audio && activeButton) setButtonState(activeButton, true);
    });
    audio.addEventListener('pause', () => {
      if (activeAudio === audio && activeButton) setButtonState(activeButton, false);
    });
    audio.addEventListener('ended', stopActive, { once: true });
    audio.addEventListener('error', stopActive, { once: true });

    try {
      await audio.play();
      setButtonState(button, true);
    } catch (error) {
      stopActive();
    }
  };

  const cancelCardNavigation = (event) => {
    const button = event.target.closest('[data-card-audio-preview]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }
  };

  document.addEventListener('pointerdown', cancelCardNavigation, true);
  document.addEventListener('mousedown', cancelCardNavigation, true);
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-card-audio-preview]');
    if (!button) return;
    cancelCardNavigation(event);
    playButton(button);
  }, true);

  const makeButton = (wrapper) => {
    if (wrapper.querySelector('[data-card-audio-preview]')) return;
    const inner = wrapper.querySelector('.card__inner');
    const link = wrapper.querySelector('.card__heading a[href*="/products/"]');
    if (!inner || !link) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'card-audio-preview';
    button.dataset.cardAudioPreview = '';
    button.dataset.productUrl = link.href;
    button.dataset.productTitle = link.textContent.trim();
    button.setAttribute('aria-pressed', 'false');
    button.setAttribute('aria-label', `Play demo: ${button.dataset.productTitle || 'product'}`);
    button.innerHTML = `
      <svg class="card-audio-preview__play" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"></path></svg>
      <svg class="card-audio-preview__pause" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7zM13 5h4v14h-4z"></path></svg>
    `;

    wrapper.addEventListener('mouseenter', () => loadDemo(button), { once: true, passive: true });
    inner.appendChild(button);
  };

  const scan = (root = document) => {
    root.querySelectorAll('.product-card-wrapper').forEach(makeButton);
  };

  const start = () => {
    scan();
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches('.product-card-wrapper')) makeButton(node);
          scan(node);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
